import { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const footerLinks = {
  Product: [
    { label: 'Features', href: '#features' },
    { label: 'For Landlords', href: '#landlords' },
    { label: 'For Tenants', href: '#tenants' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'FAQ', href: '#faq' },
  ],
  Company: [
    { label: 'About', href: '#' },
    { label: 'Contact', href: '#' },
    { label: 'Support', href: '#' },
    { label: 'Email Preferences', href: '#' },
  ],
  Legal: [
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms & Conditions', to: '/terms-and-conditions' },
  ],
};

function FooterBrand() {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <img src="/logo.png" alt="ActCheck logo" className="h-9 w-auto" data-testid="footer-logo" />
        <span className="font-heading font-bold text-lg text-white">ActCheck</span>
      </div>
      <p className="font-body text-sm text-white/50 leading-relaxed mb-5">
        Compliance, tenancy, and documentation support for landlords and tenants in England's private rented sector.
      </p>
      <div className="flex gap-3">
        <a href="#" data-testid="footer-app-store" aria-label="Download on the App Store"
          className="inline-flex items-center gap-2 bg-white/8 border border-white/10 text-white rounded-lg px-3.5 py-2 text-xs font-body hover:bg-white/12 transition-colors">
          <i className="fab fa-apple text-base" aria-hidden="true"></i>
          App Store
        </a>
        <a href="#" data-testid="footer-google-play" aria-label="Get it on Google Play"
          className="inline-flex items-center gap-2 bg-white/8 border border-white/10 text-white rounded-lg px-3.5 py-2 text-xs font-body hover:bg-white/12 transition-colors">
          <i className="fab fa-google-play text-sm" aria-hidden="true"></i>
          Google Play
        </a>
      </div>
    </div>
  );
}

function FooterLinkColumn({ title, links, onNavigate }) {
  return (
    <div>
      <h4 className="font-heading font-bold text-sm text-white mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map((link) => (
          <li key={link.label}>
            {link.to ? (
              <Link
                to={link.to}
                data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="font-body text-sm text-white/50 hover:text-white/80 transition-colors"
              >
                {link.label}
              </Link>
            ) : (
              <button
                onClick={() => onNavigate(link.href)}
                data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="font-body text-sm text-white/50 hover:text-white/80 transition-colors"
              >
                {link.label}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterBottom() {
  return (
    <div className="border-t border-white/10 pt-8">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="font-body text-xs text-white/35" data-testid="footer-disclaimer">
          ActCheck provides workflow and information support. It is not legal advice.
        </p>
        <p className="font-body text-xs text-white/35" data-testid="footer-copyright">
          &copy; {new Date().getFullYear()} ActCheck. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const scrollTo = useCallback((href) => {
    if (isHome) {
      if (href.startsWith('#') && href.length > 1) {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/' + href);
    }
  }, [isHome, navigate]);

  return (
    <footer data-testid="site-footer" className="bg-ink pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          <FooterBrand />
          {Object.entries(footerLinks).map(([title, links]) => (
            <FooterLinkColumn key={title} title={title} links={links} onNavigate={scrollTo} />
          ))}
        </div>
        <FooterBottom />
      </div>
    </footer>
  );
}
